import React, { useMemo, useState } from "react";
import { useAtom } from "jotai";

import { Table } from "components/Table";
import { Button } from "components/Button";
import { SearchInput } from "components/SearchInput";
import {
  useModal,
  ConfirmationModal,
  InfoModal,
} from "components/Modal";
import { ConductedCellRenderer } from "components/CellRenderers";
import { ItemGroupsSelectModal } from "components/ItemGroupsSelectModal";

import { DocumentsData } from "constant-data/documentsData";

import {
  docItemsAtom,
  openedDocumentAtom,
  selectedDocItemAtom,
  selectedDocumentTypeAtom,
} from "atoms/documentsAtom";

import useDocuments from "features/Documents/useDocuments";
import { mapDocItemsToDraw } from "features/Documents/docItemsToDraw";
import { DocItemModal } from "features/DocItemModal";

import styles from "./styles.module.scss";

/** Таблица позиций документа с тулбаром действий */
export const DocItemsTable = ({ columns, bottomRow }) => {
  const { removeDocItem, setOpenedDocItem, setOpenedDocItemState } =
    useDocuments();
  const { openModal2, closeModal2 } = useModal();

  const [openedDocument] = useAtom(openedDocumentAtom);
  const [selectedDocumentType] = useAtom(selectedDocumentTypeAtom);
  const [docItems] = useAtom(docItemsAtom);
  const [selectedDocItem, setSelectedDocItem] = useAtom(selectedDocItemAtom);

  const [searchVal, setSearchVal] = useState("");

  const docItemsToDraw = useMemo(() => mapDocItemsToDraw(docItems), [docItems]);

  const filteredDocItems = useMemo(() => {
    if (!searchVal) return docItemsToDraw;
    return docItemsToDraw.filter((i) =>
      i.item?.toLowerCase()?.includes(searchVal.toLowerCase())
    );
  }, [searchVal, docItemsToDraw]);

  const initDocItemState = (docItem) => {
    setOpenedDocItemState(
      docItem
        ? {
            docitemid: docItem.docitemid,
            itemid: docItem.item?.itemid,
            item2id: docItem.item2?.itemid,
            measureid: docItem.measure?.measureid,
            measure2id: docItem.measure2?.measureid,
            pricelistid: docItem.pricelist?.pricelistid,
            price: docItem.price,
            amount: docItem.amount || 0,
            realamount: docItem.realamount,
            difference:
              (Number(docItem.amount) || 0) - (Number(docItem.realamount) || 0),
            total: docItem.total,
            salepercent: docItem.salepercent,
            frontprice: docItem.frontprice,
            itemcomment: docItem.itemcomment,
          }
        : {}
    );
  };

  const { shouldCheck2Stores } =
    DocumentsData[selectedDocumentType?.id] || {};

  const onAddClick = () => {
    if (!selectedDocumentType?.id) return;

    if (
      openedDocument?.storeid &&
      (!shouldCheck2Stores || openedDocument?.store2id)
    ) {
      setOpenedDocItem(null);
      initDocItemState(null);
      openModal2(<DocItemModal />);
    } else {
      openModal2(
        <InfoModal
          title={
            !shouldCheck2Stores
              ? "Вы не можете добавлять товары, пока не выбран и не сохранён склад"
              : "Вы не можете добавлять товары, пока не выбраны и не сохранены оба склада"
          }
          onClose={closeModal2}
        />
      );
    }
  };

  const openDocItemForEdit = (docItem) => {
    if (!docItem) return;
    setOpenedDocItem({ ...docItem });
    initDocItemState(docItem);
    openModal2(<DocItemModal />);
  };

  const onEditClick = () => openDocItemForEdit(selectedDocItem);

  const onCopyClick = () => {
    if (!selectedDocItem) return;
    // Копия: то же содержимое, но как новая позиция
    setOpenedDocItem(null);
    initDocItemState(selectedDocItem);
    openModal2(<DocItemModal />);
  };

  const onDeleteClick = () => {
    if (!selectedDocItem) return;
    openModal2(
      <ConfirmationModal
        title={`Вы действительно хотите удалить этот товар ("${selectedDocItem.item?.name}")?`}
        onConfirm={removeDocItem}
        onClose={closeModal2}
      />
    );
  };

  const onFillLeftAmountClick = () => {
    if (!openedDocument?.storeid) {
      return openModal2(
        <InfoModal
          title="Вы не можете заполнять товары по остатку, пока не выбран и не сохранён склад"
          onClose={closeModal2}
        />
      );
    }
    openModal2(<ItemGroupsSelectModal />);
  };

  const onRowSelect = (row) => {
    const docItem = docItems.find((i) => i.docitemid === row.docitemid);
    setSelectedDocItem(docItem || null);
  };

  const onRowOpen = (row) => {
    const docItem = docItems.find((i) => i.docitemid === row.docitemid);
    openDocItemForEdit(docItem);
  };

  const colDefs = useMemo(
    () =>
      columns?.map((col) =>
        col.field === "isConducted"
          ? { ...col, cellRenderer: ConductedCellRenderer }
          : col
      ),
    [columns]
  );

  return (
    <div className={styles.itemsSection}>
      <div className={styles.itemsToolbar}>
        <div className={styles.itemsSearch}>
          <SearchInput
            value={searchVal}
            onChange={setSearchVal}
            placeholder="Поиск по товарам"
          />
        </div>
        <Button icon="plus" onClick={onAddClick} title="Добавить товар" />
        <Button
          icon="edit"
          onClick={onEditClick}
          disabled={!selectedDocItem}
          title="Редактировать"
        />
        <Button
          icon="delete"
          onClick={onDeleteClick}
          disabled={!selectedDocItem}
          title="Удалить"
        />
        <Button
          icon="copy"
          onClick={onCopyClick}
          disabled={!selectedDocItem}
          title="Копировать"
        />
        {selectedDocumentType?.id === "8" && (
          <Button onClick={onFillLeftAmountClick}>Заполнить по остатку</Button>
        )}
      </div>
      <Table
        height={320}
        rowData={filteredDocItems}
        colDefs={colDefs}
        rowKey="docitemid"
        selectedId={selectedDocItem?.docitemid}
        onSelect={onRowSelect}
        onOpen={onRowOpen}
        bottomRow={
          bottomRow && filteredDocItems.length
            ? bottomRow({ filteredDocItems })
            : undefined
        }
      />
    </div>
  );
};
